// admin/components/AdminLayout.js
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../Provider/AdminAuthProvider';
import { 
  FaShieldAlt, 
  FaUsers, 
  FaTachometerAlt, 
  FaCog, 
  FaSignOutAlt, 
  FaBars, 
  FaTimes,
  FaCheckCircle
} from 'react-icons/fa';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { adminLogout, getUserEmail, isAuthenticated } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        await adminLogout();
        navigate('/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: FaTachometerAlt
    },
    {
      name: 'Employer Verification',
      href: '/verification',
      icon: FaUsers
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: FaCog
    }
  ];

  const isCurrentPage = (href) => {
    return location.pathname === href;
  };

  if (!isAuthenticated()) {
    return children; // Don't show layout for login page
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Fixed for desktop */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 px-4 bg-gray-800 flex-shrink-0">
            <div className="flex items-center">
              <FaShieldAlt className="text-orange-500 text-xl mr-3" />
              <span className="text-white font-semibold">Admin Portal</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <FaTimes />
            </button>
          </div>

          {/* Navigation - Flex grow to take available space */}
          <nav className="mt-8 flex-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                    isCurrentPage(item.href)
                      ? 'bg-orange-500 text-white border-r-4 border-orange-300'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info and logout - Fixed at bottom */}
          <div className="p-4 bg-gray-800 flex-shrink-0">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <FaCheckCircle className="text-white text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {getUserEmail()}
                </p>
                <p className="text-xs text-gray-400">Administrator</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
            >
              <FaSignOutAlt className="mr-2 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden text-gray-500 hover:text-gray-700 mr-4"
                >
                  <FaBars className="h-6 w-6" />
                </button>
                <h1 className="text-xl font-semibold text-gray-900">
                  {navigation.find(item => isCurrentPage(item.href))?.name || 'Admin Portal'}
                </h1>
              </div>
              
              {/* Desktop user menu */}
              <div className="hidden lg:flex items-center space-x-4">
                <span className="text-sm text-gray-600">{getUserEmail()}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  <FaSignOutAlt className="mr-1 h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content - Scrollable */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;